import { useEffect, useState } from "react";
import { Plus, Users, QrCode, Trash2, UserPlus, Link2, Copy, UserMinus, Mail, MessageCircle, Wrench, Phone, Cake, KeyRound, X, ChevronRight, ChevronDown, ShieldCheck, Pencil, BellRing, Info } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle, EmptyState, Pill } from "@/components/Brutal";
import { DatePicker } from "@/components/DatePicker";
import { MemberNotifEditor } from "@/components/MemberNotifPrefs";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { useConfirm } from "@/components/ConfirmProvider";
import { ROLE_LABELS } from "@/lib/labels";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COLORS = ["#FF6B00", "#0057FF", "#4ADE80", "#FFD54F", "#FF66A1"];

export default function Groepen() {
  const { user, orgs, activeOrg } = useAuth();
  const t = useT();
  const confirm = useConfirm();
  const admin = isAdmin(user);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [qr, setQr] = useState(null);
  const [viewGroup, setViewGroup] = useState(null);
  const [tab, setTab] = useState("gebruikers");
  const [delUser, setDelUser] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [pForm, setPForm] = useState({ name: "", email: "", phone: "", birthdate: "", pin: "" });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", pin: "", phone: "", birthdate: "", role: "gebruiker", group_ids: [] });
  const orgId = activeOrg;
  const joinPath = `/aanmelden${orgId ? `?org=${orgId}` : ""}`;
  const joinLink = `${window.location.origin}${joinPath}`;
  const joinQr = `${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(joinPath)}`;
  const orgName = orgs?.find((o) => o.org_id === activeOrg)?.name || t("deze organisatie");

  const load = async () => {
    const [g, u] = await Promise.all([api.get("/groups"), api.get("/users")]);
    setGroups(g.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditId(null); setName(""); setColor(COLORS[0]); setOpen(true); };
  const openEdit = (g) => { setEditId(g.id); setName(g.name); setColor(g.color || COLORS[0]); setOpen(true); };

  const save = async () => {
    if (!name) return toast.error(t("Naam verplicht"));
    if (editId) {
      await api.put(`/groups/${editId}`, { name, color });
      toast.success(t("Groep bijgewerkt"));
    } else {
      await api.post("/groups", { name, color });
      toast.success(t("Groep aangemaakt"));
    }
    setOpen(false); setEditId(null); setName(""); load();
  };

  const invite = async (g) => {
    const { data } = await api.post(`/groups/${g.id}/invite`);
    const base = window.location.origin;
    const { data: qrData } = await api.get(`/invites/${data.code}/qr`, { params: { base_url: base } });
    setQr({ ...qrData, group: g });
  };

  const activate = async (u) => {
    if (!u.email) return toast.error(t("Voeg eerst een e-mailadres toe om te kunnen activeren"));
    try {
      await api.post(`/users/${u.user_id}/activate`, { base_url: window.location.origin });
      toast.success(t("Gebruiker geactiveerd — e-mail verstuurd"));
      setProfileUser(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Activeren mislukt")); }
  };

  const remind = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/remind`, { base_url: window.location.origin });
      toast.success(t("Herinnering verstuurd"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Versturen mislukt")); }
  };

  const toggleGroupMember = async (u, gid) => {
    const has = u.group_ids?.includes(gid);
    const gids = has ? (u.group_ids || []).filter((x) => x !== gid) : [...(u.group_ids || []), gid];
    try {
      await api.put(`/users/${u.user_id}`, { group_ids: gids });
      await load();
      toast.success(has ? `${u.name} ${t("uit groep gehaald")}` : `${u.name} ${t("toegevoegd aan groep")}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const setRole = async (u, role) => {
    try { await api.put(`/users/${u.user_id}`, { role }); await load(); toast.success(`${t("Rol bijgewerkt:")} ${t(ROLE_LABELS[role]) || role}`); }
    catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const toggleFacilitair = async (u) => {
    const cur = u.extra_roles || [];
    const next = cur.includes("facilitair") ? cur.filter((x) => x !== "facilitair") : [...cur, "facilitair"];
    try { await api.put(`/users/${u.user_id}`, { extra_roles: next }); await load(); toast.success(next.includes("facilitair") ? t("Facilitair aangezet") : t("Facilitair uitgezet")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const enableAllNotifs = async (u) => {
    if (!(await confirm({ title: t("Alle meldingen aanzetten?"), description: t("Zet álle meldingen aan — push, e-mail én SMS — voor deze persoon en schakelt stille uren uit. Handig als iemand slecht reageert."), confirmLabel: t("Aanzetten"), tone: "info" }))) return;
    try { await api.post(`/users/${u.user_id}/enable-all-notifs`); await load(); toast.success(t("Alle meldingen aangezet")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const saveProfile = async (pu) => {
    if (!pForm.name.trim()) { toast.error(t("Naam verplicht")); return; }
    if (pForm.pin && !/^\d{4,6}$/.test(pForm.pin)) { toast.error(t("Pincode moet 4 tot 6 cijfers zijn")); return; }
    try {
      const payload = { name: pForm.name.trim(), phone: pForm.phone || "", birthdate: pForm.birthdate || "" };
      if (pForm.email && pForm.email.trim()) payload.email = pForm.email.trim();
      const { data } = await api.put(`/users/${pu.user_id}`, payload);
      if (pForm.pin) await api.post(`/users/${pu.user_id}/set-pin`, { pin: pForm.pin });
      await load();
      setProfileUser((p) => (p ? { ...p, ...data, has_pin: pForm.pin ? true : p.has_pin } : p));
      setEditProfile(false);
      toast.success(t("Gegevens bijgewerkt"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const removePin = async (pu) => {
    if (!(await confirm({ title: t("Pincode verwijderen?"), description: `${pu.name} ${t("kan daarna geen kiosk-acties meer met een pincode doen.")}`, confirmLabel: t("Verwijderen"), tone: "danger" }))) return;
    try {
      await api.post(`/users/${pu.user_id}/set-pin`, { pin: "" });
      await load();
      setProfileUser((p) => (p ? { ...p, has_pin: false } : p));
      setPForm((f) => ({ ...f, pin: "" }));
      toast.success(t("Pincode verwijderd"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const createUser = async () => {
    if (!form.name) return toast.error(t("Naam verplicht"));
    try {
      await api.post("/users", { ...form, email: form.email || null, pin: form.pin || null, phone: form.phone || null, birthdate: form.birthdate || null });
      setForm({ name: "", email: "", pin: "", phone: "", birthdate: "", role: "gebruiker", group_ids: [] });
      setInviteOpen(false); await load(); toast.success(t("Persoon toegevoegd"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const deleteUser = (u) => {
    if (u.user_id === user.user_id) return;
    setDelUser(u);
  };

  const performDelete = async (mode) => {
    if (!delUser) return;
    setDelBusy(true);
    try {
      await api.delete(`/users/${delUser.user_id}`, { params: { mode } });
      await load();
      toast.success(mode === "account" ? `${delUser.name} — ${t("account verwijderd")}` : `${delUser.name} ${t("uit")} ${orgName} ${t("verwijderd")}`);
      setDelUser(null);
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setDelBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="groepen-page">
      <div className="flex items-center justify-between">
        <SectionTitle sub={`${groups.length} groepen · ${users.length} gebruikers`}>Groepen & gebruikers</SectionTitle>
        {admin && tab === "groepen" && <BrutalButton data-testid="new-group-btn" onClick={openCreate}><Plus className="w-5 h-5" strokeWidth={3} />Nieuw</BrutalButton>}
        {admin && tab === "gebruikers" && <BrutalButton data-testid="invite-user-btn" onClick={() => setInviteOpen(true)}><UserPlus className="w-5 h-5" strokeWidth={3} />{t("Uitnodigen")}</BrutalButton>}
      </div>

      <div className="flex gap-2 brutal-sm rounded-full bg-white p-1">
        {[["groepen", "Groepen"], ["gebruikers", "Gebruikers"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`flex-1 rounded-full px-4 py-2 font-head font-bold text-sm transition-colors ${tab === k ? "bg-brand-violet text-white" : ""}`}>{t(l)}</button>
        ))}
      </div>

      {tab === "gebruikers" && (
        <div className="space-y-2" data-testid="users-list">
          {users.map((u) => {
            const gs = groups.filter((g) => u.group_ids?.includes(g.id));
            const isFac = (u.extra_roles || []).includes("facilitair");
            return (
              <button key={u.user_id} data-testid={`user-row-${u.user_id}`} onClick={() => setProfileUser(u)}
                className="w-full text-left">
                <BrutalCard className="brutal-press">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-brand-cream flex items-center justify-center font-head font-black text-lg shrink-0 overflow-hidden">
                      {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-head font-bold leading-tight truncate">{u.name}</h3>
                      <p className="text-xs text-brand-ink/50 truncate">{u.email || t("PIN-gebruiker")}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Pill className="bg-brand-violet/15">{t(ROLE_LABELS[u.role]) || u.role}</Pill>
                        {u.status === "invited" && <Pill className="bg-brand-yellow text-brand-ink" data-testid={`invited-pill-${u.user_id}`}>{t("Uitgenodigd")}</Pill>}
                        {isFac && <Pill className="bg-brand-green/20 text-brand-ink">{t("Facilitair")}</Pill>}
                        {gs.map((g) => <Pill key={g.id} className="bg-brand-cream">{g.name}</Pill>)}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-brand-ink/30 shrink-0" strokeWidth={2.5} />
                  </div>
                </BrutalCard>
              </button>
            );
          })}
        </div>
      )}

      {tab === "groepen" && (groups.length === 0 ? <EmptyState icon={Users} title={t("Nog geen groepen")} text={admin ? t("Maak een groep aan om te beginnen.") : t("Vraag een beheerder om je toe te voegen.")} /> : (
        <div className="space-y-3">
          {groups.map((g) => {
            const members = users.filter((u) => u.group_ids?.includes(g.id));
            return (
              <BrutalCard key={g.id} data-testid={`group-${g.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 brutal-sm rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: g.color }}>
                    <Users className="w-6 h-6" strokeWidth={2.5} />
                  </div>
                  <button data-testid={`open-group-${g.id}`} onClick={() => setViewGroup(g)} className="flex-1 text-left">
                    <h3 className="font-head font-bold text-lg leading-tight">{g.name}</h3>
                    <p className="text-xs font-semibold text-brand-ink/50">{members.length} {t("leden · bekijk")}</p>
                  </button>
                  {admin && (
                    <div className="flex gap-1.5">
                      <button data-testid={`edit-group-${g.id}`} onClick={() => openEdit(g)} className="brutal-sm brutal-press rounded-full p-2 bg-brand-yellow"><Pencil className="w-4 h-4" strokeWidth={2.5} /></button>
                      <button data-testid={`invite-${g.id}`} onClick={() => invite(g)} className="brutal-sm brutal-press rounded-full p-2 bg-brand-green"><QrCode className="w-4 h-4" strokeWidth={2.5} /></button>
                      <button data-testid={`delete-group-${g.id}`} onClick={async () => { if (await confirm({ title: t("Groep verwijderen?"), description: t("De groep wordt verwijderd.") })) api.delete(`/groups/${g.id}`).then(load); }} className="brutal-sm brutal-press rounded-full p-2 bg-white"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                    </div>
                  )}
                </div>
                {members.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {members.map((m) => <Pill key={m.user_id} className="bg-brand-cream">{m.name}</Pill>)}
                  </div>
                )}
              </BrutalCard>
            );
          })}
        </div>
      ))}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditId(null); }}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{editId ? t("Groep hernoemen") : t("Nieuwe groep")}</DialogTitle></DialogHeader>
          <input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Bijv. Keuken")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <div className="flex gap-2">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} className={`w-9 h-9 rounded-full border-2 border-brand-ink ${color === c ? "ring-2 ring-brand-ink ring-offset-2" : ""}`} style={{ backgroundColor: c }} />
            ))}
          </div>
          <BrutalButton data-testid="create-group-btn" onClick={save} className="w-full">{editId ? t("Opslaan") : t("Aanmaken")}</BrutalButton>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qr} onOpenChange={() => setQr(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><UserPlus className="w-5 h-5" strokeWidth={2.5} />{t("Nodig uit voor")} {qr?.group?.name}</DialogTitle></DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-3">
              <img src={qr.qr} alt="QR" className="w-56 h-56 brutal rounded-xl" data-testid="invite-qr" />
              <div className="w-full brutal-sm rounded-xl px-3 py-2 bg-brand-cream flex items-center gap-2">
                <Link2 className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                <span className="text-xs font-mono truncate flex-1">{qr.link}</span>
                <button onClick={() => { navigator.clipboard.writeText(qr.link); toast.success(t("Gekopieerd")); }} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><Copy className="w-3.5 h-3.5" strokeWidth={2.5} /></button>
              </div>
              <p className="text-sm text-brand-ink/60 text-center">{t("Scan de QR-code of deel de link om lid te worden.")}</p>
              <div className="w-full brutal-sm rounded-xl bg-brand-yellow/40 p-3 flex items-start gap-2" data-testid="invite-members-only">
                <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5} />
                <p className="text-xs font-semibold">{t("Alleen voor bestaande huisgenoten. Nieuwe leden moeten eerst door een beheerder worden goedgekeurd via 'Aansluiten bij een organisatie'.")}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!viewGroup} onOpenChange={() => setViewGroup(null)}>
        <DialogContent data-testid="group-members-dialog" className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: viewGroup?.color }}><Users className="w-4 h-4" strokeWidth={2.5} /></span>
            {t("Leden van")} {viewGroup?.name}
          </DialogTitle></DialogHeader>
          {viewGroup && (() => {
            const members = users.filter((u) => u.group_ids?.includes(viewGroup.id));
            const nonMembers = users.filter((u) => !u.group_ids?.includes(viewGroup.id));
            return (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-bold text-brand-ink/50 uppercase tracking-wide mb-1.5">{t("Leden")} ({members.length})</p>
                  {members.length === 0 ? <p className="text-sm text-brand-ink/50">{t("Nog geen leden in deze groep.")}</p> : (
                    <div className="space-y-2">
                      {members.map((m) => (
                        <div key={m.user_id} data-testid={`member-${m.user_id}`} className="brutal-sm rounded-xl px-3 py-2.5 bg-white flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-brand-cream flex items-center justify-center font-head font-black shrink-0">{m.name?.[0]?.toUpperCase()}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold leading-tight truncate">{m.name}</p>
                            <p className="text-xs text-brand-ink/50 truncate">{m.email || t("PIN-gebruiker")}</p>
                          </div>
                          <Pill className="bg-brand-violet/15">{t(ROLE_LABELS[m.role]) || m.role}</Pill>
                          {admin && (
                            <button data-testid={`remove-member-${m.user_id}`} onClick={() => toggleGroupMember(m, viewGroup.id)}
                              className="brutal-sm brutal-press rounded-full p-1.5 bg-brand-pink text-white shrink-0"><UserMinus className="w-4 h-4" strokeWidth={2.5} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {admin && nonMembers.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-brand-ink/50 uppercase tracking-wide mb-1.5">{t("Toevoegen aan groep")}</p>
                    <div className="space-y-2">
                      {nonMembers.map((m) => (
                        <div key={m.user_id} data-testid={`nonmember-${m.user_id}`} className="brutal-sm rounded-xl px-3 py-2.5 bg-brand-cream flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center font-head font-black shrink-0">{m.name?.[0]?.toUpperCase()}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold leading-tight truncate">{m.name}</p>
                            <p className="text-xs text-brand-ink/50 truncate">{m.email || t("PIN-gebruiker")}</p>
                          </div>
                          <button data-testid={`add-member-${m.user_id}`} onClick={() => toggleGroupMember(m, viewGroup.id)}
                            className="brutal-sm brutal-press rounded-full p-1.5 bg-brand-green shrink-0"><UserPlus className="w-4 h-4" strokeWidth={2.5} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!profileUser} onOpenChange={(o) => { if (!o) { setProfileUser(null); setEditProfile(false); } }}>
        <DialogContent data-testid="profile-dialog" className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogTitle className="sr-only">{profileUser?.name || t("Profiel")}</DialogTitle>
          {profileUser && (() => {
            const pu = users.find((x) => x.user_id === profileUser.user_id) || profileUser;
            const isFac = (pu.extra_roles || []).includes("facilitair");
            const pg = groups.filter((g) => pu.group_ids?.includes(g.id));
            const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }); } catch { return d; } };
            return (
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-brand-cream flex items-center justify-center font-head font-black text-2xl shrink-0 overflow-hidden">
                    {pu.avatar_url ? <img src={pu.avatar_url} alt="" className="w-full h-full object-cover" /> : pu.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-head font-black text-xl leading-tight truncate">{pu.name}</h2>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Pill className="bg-brand-violet/15">{t(ROLE_LABELS[pu.role]) || pu.role}</Pill>
                      {isFac && <Pill className="bg-brand-green/20">{t("Facilitair")}</Pill>}
                    </div>
                  </div>
                </div>

                {admin && editProfile ? (
                  <div className="mt-4 space-y-2" data-testid="profile-edit-form">
                    <div>
                      <label className="text-xs font-bold text-brand-ink/50">{t("Naam")}</label>
                      <input data-testid="profile-edit-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-brand-ink/50">{t("E-mail")}</label>
                      <input data-testid="profile-edit-email" type="email" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} placeholder={t("Geen e-mail (PIN-gebruiker)")} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-brand-ink/50">{t("Telefoon")}</label>
                      <input data-testid="profile-edit-phone" inputMode="tel" value={pForm.phone} onChange={(e) => setPForm({ ...pForm, phone: e.target.value })} placeholder={t("Geen telefoonnummer")} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-brand-ink/50">{t("Geboortedatum")}</label>
                      <DatePicker testid="profile-edit-birthdate" value={pForm.birthdate || ""} onChange={(v) => setPForm({ ...pForm, birthdate: v })} fromYear={1920} toYear={new Date().getFullYear()} placeholder={t("Geboortedatum")} className="px-3 py-2.5" />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <BrutalButton data-testid="profile-save-btn" size="sm" className="flex-1" onClick={() => saveProfile(pu)}>{t("Opslaan")}</BrutalButton>
                      <BrutalButton data-testid="profile-cancel-btn" size="sm" variant="white" onClick={() => setEditProfile(false)}>{t("Annuleren")}</BrutalButton>
                    </div>
                  </div>
                ) : (
                <div className="mt-4 space-y-2" data-testid="profile-details">
                  <div className="flex items-center gap-3 brutal-sm rounded-xl px-3 py-2.5 bg-brand-cream/50">
                    <Mail className="w-4 h-4 shrink-0 text-brand-ink/50" strokeWidth={2.5} />
                    <span className="text-sm font-semibold truncate">{pu.email || t("Geen e-mail (PIN-gebruiker)")}</span>
                  </div>
                  <div className="flex items-center gap-3 brutal-sm rounded-xl px-3 py-2.5 bg-brand-cream/50">
                    <Phone className="w-4 h-4 shrink-0 text-brand-ink/50" strokeWidth={2.5} />
                    <span className="text-sm font-semibold truncate">{pu.phone || t("Geen telefoonnummer")}</span>
                  </div>
                  <div className="flex items-center gap-3 brutal-sm rounded-xl px-3 py-2.5 bg-brand-cream/50">
                    <Cake className="w-4 h-4 shrink-0 text-brand-ink/50" strokeWidth={2.5} />
                    <span className="text-sm font-semibold truncate">{pu.birthdate ? fmtDate(pu.birthdate) : t("Geen geboortedatum")}</span>
                  </div>
                  <div className="flex items-center gap-3 brutal-sm rounded-xl px-3 py-2.5 bg-brand-cream/50">
                    <KeyRound className="w-4 h-4 shrink-0 text-brand-ink/50" strokeWidth={2.5} />
                    <span className="text-sm font-semibold">{pu.has_pin ? t("Pincode ingesteld") : t("Geen pincode")}</span>
                  </div>
                  <div className="flex items-start gap-3 brutal-sm rounded-xl px-3 py-2.5 bg-brand-cream/50">
                    <Users className="w-4 h-4 shrink-0 mt-0.5 text-brand-ink/50" strokeWidth={2.5} />
                    <span className="text-sm font-semibold">{pg.length ? pg.map((g) => g.name).join(", ") : t("Geen groepen")}</span>
                  </div>
                  {admin && (
                    <button data-testid={`profile-edit-toggle-${pu.user_id}`}
                      onClick={() => { setPForm({ name: pu.name || "", email: pu.email || "", phone: pu.phone || "", birthdate: pu.birthdate || "" }); setEditProfile(true); }}
                      className="w-full brutal-sm brutal-press rounded-xl px-3 py-2 bg-brand-yellow text-brand-ink text-sm font-bold flex items-center gap-2 justify-center">
                      <Pencil className="w-4 h-4" strokeWidth={2.5} />{t("Gegevens bewerken")}
                    </button>
                  )}
                </div>
                )}

                {admin && (
                  <div className="mt-4 pt-4 border-t-2 border-dashed border-brand-ink/10 space-y-3">
                    <p className="text-xs font-black uppercase tracking-wide text-brand-ink/40">{t("Beheer")}</p>
                    {pu.status === "invited" && (
                      <div className="brutal-sm rounded-xl p-3 bg-brand-yellow/25 space-y-2" data-testid={`activate-block-${pu.user_id}`}>
                        <p className="text-sm font-bold text-brand-ink">{t("Deze persoon is uitgenodigd maar nog niet actief.")}</p>
                        <button data-testid={`activate-user-${pu.user_id}`} onClick={() => activate(pu)}
                          className="brutal-sm brutal-press rounded-xl px-3 py-2 bg-brand-green text-white text-sm font-bold flex items-center gap-2 w-full justify-center">
                          <ShieldCheck className="w-4 h-4" strokeWidth={2.5} />{t("Activeer nu (stuur e-mail)")}
                        </button>
                        {pu.email && (
                          <button data-testid={`remind-user-${pu.user_id}`} onClick={() => remind(pu)}
                            className="brutal-sm brutal-press rounded-xl px-3 py-2 bg-white text-brand-ink text-sm font-bold flex items-center gap-2 w-full justify-center">
                            <Mail className="w-4 h-4" strokeWidth={2.5} />{t("Herinnering sturen")}
                          </button>
                        )}
                        {!pu.email && <p className="text-xs font-semibold text-brand-ink/60">{t("Voeg eerst een e-mailadres toe om te kunnen activeren.")}</p>}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5" data-testid={`profile-roles-${pu.user_id}`}>
                      <span className="text-xs font-bold text-brand-ink/50 mr-1">{t("Rol:")}</span>
                      {["gebruiker", "beheerder"].map((r) => (
                        <button key={r} data-testid={`profile-role-${pu.user_id}-${r}`} onClick={() => pu.role !== r && setRole(pu, r)}
                          className={`brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold ${pu.role === r ? "bg-brand-blue text-white" : "bg-white"}`}>{t(ROLE_LABELS[r])}</button>
                      ))}
                      <button data-testid={`profile-facilitair-${pu.user_id}`} onClick={() => toggleFacilitair(pu)}
                        className={`brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1 ${isFac ? "bg-brand-green text-white" : "bg-white"}`}>
                        <Wrench className="w-3 h-3" strokeWidth={2.5} />{t("Facilitair")}
                      </button>
                    </div>
                    {groups.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5" data-testid={`profile-groups-${pu.user_id}`}>
                        <span className="text-xs font-bold text-brand-ink/50 mr-1">{t("Groepen:")}</span>
                        {groups.map((g) => {
                          const on = pu.group_ids?.includes(g.id);
                          return (
                            <button key={g.id} data-testid={`profile-group-${pu.user_id}-${g.id}`} onClick={() => toggleGroupMember(pu, g.id)}
                              className={`brutal-sm brutal-press rounded-full px-2.5 py-0.5 text-xs font-bold ${on ? "text-white" : "bg-white opacity-60"}`}
                              style={on ? { backgroundColor: g.color } : {}}>{g.name}</button>
                          );
                        })}
                      </div>
                    )}
                    {admin && (
                      <div className="brutal-sm rounded-xl bg-brand-cream/40 overflow-hidden" data-testid={`profile-notif-section-${pu.user_id}`}>
                        <button data-testid={`profile-notif-toggle-${pu.user_id}`} onClick={() => setNotifOpen((o) => !o)}
                          className="w-full flex items-center justify-between px-3 py-2.5 font-bold text-sm">
                          <span className="flex items-center gap-2"><BellRing className="w-4 h-4" strokeWidth={2.5} />{t("Meldingen per kanaal")}</span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${notifOpen ? "rotate-180" : ""}`} strokeWidth={2.5} />
                        </button>
                        {notifOpen && <div className="px-3 pb-3 border-t-2 border-dashed border-brand-ink/10 pt-2"><MemberNotifEditor userId={pu.user_id} /></div>}
                      </div>
                    )}
                    {admin && (
                      <button data-testid={`profile-enable-notifs-${pu.user_id}`} onClick={() => enableAllNotifs(pu)}
                        className="brutal-sm brutal-press rounded-xl px-3 py-2 bg-brand-yellow text-brand-ink text-sm font-bold flex items-center gap-2 w-full justify-center">
                        <BellRing className="w-4 h-4" strokeWidth={2.5} />{t("Alle meldingen aanzetten")}
                      </button>
                    )}
                    {pu.user_id !== user.user_id && (
                      <button data-testid={`profile-delete-${pu.user_id}`} onClick={() => { setProfileUser(null); deleteUser(pu); }}
                        className="brutal-sm brutal-press rounded-xl px-3 py-2 bg-brand-pink text-white text-sm font-bold flex items-center gap-2 w-full justify-center">
                        <Trash2 className="w-4 h-4" strokeWidth={2.5} />{t("Verwijderen uit")} {orgName}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog" className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><UserPlus className="w-5 h-5" strokeWidth={2.5} />{t("Persoon uitnodigen")}</DialogTitle></DialogHeader>

          <div className="space-y-3">
            <p className="text-sm font-bold text-brand-ink/70">{t("Deel de uitnodiging")}</p>
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <img src={joinQr} alt="QR" className="w-32 h-32 brutal-sm rounded-xl bg-white p-1 shrink-0" data-testid="invite-join-qr" />
              <div className="flex-1 w-full space-y-2">
                <div className="brutal-sm rounded-xl px-3 py-2 bg-brand-cream flex items-center gap-2">
                  <Link2 className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                  <span className="text-xs font-mono truncate flex-1">{joinLink}</span>
                  <button data-testid="invite-copy-link" onClick={() => { navigator.clipboard?.writeText(joinLink); toast.success(t("Gekopieerd")); }} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><Copy className="w-3.5 h-3.5" strokeWidth={2.5} /></button>
                </div>
                <div className="flex gap-2">
                  <BrutalButton data-testid="invite-whatsapp" variant="green" size="sm" className="flex-1" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Sluit je aan bij ${orgName} op Slackr\n${joinLink}`)}`, "_blank")}><MessageCircle className="w-4 h-4" strokeWidth={2.5} />WhatsApp</BrutalButton>
                  <BrutalButton data-testid="invite-email" variant="pink" size="sm" className="flex-1" onClick={() => window.open(`mailto:?subject=${encodeURIComponent(`Uitnodiging voor ${orgName}`)}&body=${encodeURIComponent(`Hoi!\n\nSluit je aan bij ${orgName} op Slackr:\n${joinLink}\n\nTot snel!`)}`)}><Mail className="w-4 h-4" strokeWidth={2.5} />E-mail</BrutalButton>
                </div>
              </div>
            </div>
          </div>

          <div className="my-2 flex items-center gap-2 text-xs font-bold text-brand-ink/40">
            <span className="flex-1 h-px bg-brand-ink/10" />{t("of voeg direct toe")}<span className="flex-1 h-px bg-brand-ink/10" />
          </div>

          <input data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("Naam")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <input data-testid="invite-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("E-mail (optioneel)")} className="w-full brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="invite-pin-input" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder={t("Pincode (optioneel)")} inputMode="numeric" className="brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
            <input data-testid="invite-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("Telefoon")} inputMode="tel" className="brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-xs font-bold text-brand-ink/50 self-center mr-1">{t("Rol:")}</span>
            {["gebruiker", "beheerder"].map((r) => (
              <button key={r} data-testid={`invite-role-${r}`} onClick={() => setForm({ ...form, role: r })}
                className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-sm font-bold ${form.role === r ? "bg-brand-blue text-white" : "bg-white"}`}>{t(ROLE_LABELS[r])}</button>
            ))}
          </div>
          {groups.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <span className="text-xs font-bold text-brand-ink/50 self-center mr-1">{t("Groepen:")}</span>
              {groups.map((g) => {
                const on = form.group_ids.includes(g.id);
                return (
                  <button key={g.id} data-testid={`invite-group-${g.id}`} onClick={() => setForm({ ...form, group_ids: on ? form.group_ids.filter((x) => x !== g.id) : [...form.group_ids, g.id] })}
                    className={`brutal-sm brutal-press rounded-full px-2.5 py-0.5 text-xs font-bold ${on ? "text-white" : "bg-white opacity-60"}`}
                    style={on ? { backgroundColor: g.color } : {}}>{g.name}</button>
                );
              })}
            </div>
          )}
          <BrutalButton data-testid="invite-create-btn" onClick={createUser} className="w-full">{t("Toevoegen")}</BrutalButton>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Trash2 className="w-5 h-5" strokeWidth={2.5} />{delUser?.name} {t("verwijderen")}</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/70">{t("Hoe wil je")} <b>{delUser?.name}</b> {t("verwijderen")}?</p>
          <button data-testid="delete-mode-org" disabled={delBusy} onClick={() => performDelete("org")}
            className="w-full brutal-sm brutal-press rounded-xl p-3 bg-white text-left flex items-start gap-3 disabled:opacity-50">
            <UserMinus className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5} />
            <span><span className="font-head font-bold block">{t("Alleen uit")} {orgName}</span><span className="text-xs text-brand-ink/60">{t("De persoon blijft lid van eventuele andere huishoudens.")}</span></span>
          </button>
          <button data-testid="delete-mode-account" disabled={delBusy} onClick={() => performDelete("account")}
            className="w-full brutal-sm brutal-press rounded-xl p-3 bg-brand-pink text-white text-left flex items-start gap-3 disabled:opacity-50">
            <Trash2 className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5} />
            <span><span className="font-head font-bold block">{t("Account volledig verwijderen")}</span><span className="text-xs text-white/80">{t("Definitief. Verwijdert de persoon uit")} <b>{t("álle")}</b> {t("huishoudens.")}</span></span>
          </button>
          <button data-testid="delete-cancel" onClick={() => setDelUser(null)} className="w-full text-sm font-bold text-brand-ink/60 py-1">{t("Annuleren")}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
